// DivisionPage "Standings" view (INT-0055, owner 2026-10-30): the public
// standings table, inside the admin. Ordering comes from the shared
// createStandings engine and columns / legend / formatting from the shared
// standingsView model (ADR 0071) — the SAME inputs the public Standings tab
// renders, so the two can never disagree. Only the markup is admin (shadcn
// tokens, ADR 0057). Regular-season games only; the bracket lives in the
// Schedule view.
import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Table, TableHeader, TableBody, TableHead, TableRow, TableCell,
} from '@/components/ui/table';
import { ListOrdered, ExternalLink } from 'lucide-react';
import getTeamDisplayName from '@staty/shared/utils/getTeamDisplayName';
import { buildStandingsView } from '@staty/shared/utils/standingsView';

interface DivisionStandingsProps {
  divisionGames: any[];
  divisionTeams: any[];
  /** Resolved (hierarchy) config: server effectiveConfig when present. */
  config: any;
  sport: string;
  /** Absolute URL of the public standings page, or null when unknown. */
  publicStandingsUrl?: string | null;
}

const record = (row: any) => `${row?.win ?? 0}-${row?.loss ?? 0}-${row?.tie ?? 0}`;

export default function DivisionStandings({
  divisionGames, divisionTeams, config, sport, publicStandingsUrl = null,
}: DivisionStandingsProps) {
  const view = useMemo(
    () => buildStandingsView({ games: divisionGames, teams: divisionTeams, config, sport }),
    [divisionGames, divisionTeams, config, sport],
  );
  const { groups, columns, fromTiebreakers, legend, hasResults } = view;

  return (
    <Card data-testid="division-standings">
      <CardHeader>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="space-y-1.5">
            <CardTitle className="flex items-center gap-2">
              <ListOrdered className="h-5 w-5" />
              Standings
            </CardTitle>
            <CardDescription>
              Regular-season games only, in the same order as your public site.
            </CardDescription>
          </div>
          {publicStandingsUrl && (
            <Button variant="outline" size="sm" asChild data-tour="division-standings-view-on-site">
              <a href={publicStandingsUrl} target="_blank" rel="noopener noreferrer" aria-label="View standings on the public site">
                <ExternalLink className="h-4 w-4 mr-2" />
                View on site
              </a>
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {view.rows.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground" data-testid="division-standings-empty">
            No standings yet. Add teams and enter scores, and the table fills in here.
          </p>
        ) : (
          groups.map(({ subDivision, rows }) => (
            <div key={subDivision === null ? '__all' : subDivision} className="space-y-2">
              {subDivision !== null && (
                <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{subDivision}</div>
              )}
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10 text-center">#</TableHead>
                      <TableHead>Team</TableHead>
                      <TableHead className="w-20 text-right">W-L-T</TableHead>
                      {columns.map((column) => (
                        <TableHead key={column.key} className="w-16 text-right" title={column.description}>
                          {column.label}
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.map((row: any, index: number) => {
                      const pdNum = Number(row?.pd);
                      return (
                        <TableRow key={row._id || index} data-testid="division-standings-row">
                          <TableCell className={`text-center tabular-nums ${index === 0 ? 'font-bold' : 'text-muted-foreground'}`}>
                            {index + 1}
                          </TableCell>
                          <TableCell className="font-medium">{getTeamDisplayName(row) || 'TBD'}</TableCell>
                          <TableCell className="text-right tabular-nums">{record(row)}</TableCell>
                          {columns.map((column) => {
                            // Default PD column keeps the public semantic coloring
                            // (green up, red down); everything else is plain.
                            const tone = !fromTiebreakers && column.key === 'pd'
                              ? (pdNum > 0 ? 'text-green-600' : pdNum < 0 ? 'text-red-600' : 'text-muted-foreground')
                              : '';
                            return (
                              <TableCell key={column.key} className={`text-right tabular-nums ${tone}`}>
                                {hasResults ? column.format(row) : ''}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </div>
          ))
        )}

        {legend ? (
          <div className="rounded-lg border bg-muted/40 px-4 py-3 text-sm" data-testid="division-standings-legend">
            <div className="mb-2 font-semibold">Tiebreaker Order</div>
            <ol className="ml-5 list-decimal space-y-1">
              {legend.map((item) => (
                <li key={item.label}>
                  <strong>{item.label}</strong>
                  {': '}
                  {item.description}
                </li>
              ))}
            </ol>
            <div className="mt-2 text-xs italic text-muted-foreground">
              Tiebreakers are applied in order until a winner is determined.
            </div>
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">
            No tiebreakers configured for this sport, so ties break by wins, then point differential, then points allowed.
            Set tiebreakers from{' '}
            <Link to="/admin/organize" className="text-primary underline" data-tour="division-standings-tiebreakers-link">Competitions</Link>
            {' '}(the row menu, Tiebreakers).
          </p>
        )}

        {view.showPlayoffTieNote ? (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="division-standings-playoff-note">
            <strong>Playoff ties:</strong>
            {' '}
            a playoff game that ends level is awarded to the team placed higher
            in the standings above; a game between teams with no standings
            position stays undecided.
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
